import { useEffect, useState } from 'react';
import {
    EnrichedSavedPropertyId,
    ListingCategory
} from '../../../graphql.generated';
import { useGetEnrichedSavedPropertyIdsQuery } from './EnrichedSavedPropertyIds.query.generated';

type SavedListingSection = 'for-sale' | 'to-rent' | 'overseas' | 'commercial';

const getListingSection = ({
    section,
    category
}: {
    section?: string | null;
    category?: ListingCategory | null;
}): SavedListingSection | null => {
    if (!section) {
        return null;
    }
    if (
        ['for-sale', 'new-homes'].includes(section) &&
        category === 'residential'
    ) {
        return 'for-sale';
    } else if (section === 'to-rent' && category === 'residential') {
        return 'to-rent';
    } else if (category === 'commercial') {
        return 'commercial';
    } else if (section === 'overseas') {
        return 'overseas';
    }
    return 'for-sale';
};

export type MappedEnrichedSavedPropertyId = Pick<
    EnrichedSavedPropertyId,
    'listingId' | 'savedDate' | 'publicationStatus'
> & {
    section: SavedListingSection | null;
};

export const mapEnrichedSavedPropertyId = (
    savedListing: EnrichedSavedPropertyId
): MappedEnrichedSavedPropertyId => {
    return {
        ...savedListing,
        section: getListingSection({
            section: savedListing.section,
            category: savedListing.category
        })
    };
};

export const useEnrichedSavedPropertyIds = (): {
    savedListings?: Array<MappedEnrichedSavedPropertyId>;
    loading: boolean;
    hasError: boolean;
} => {
    const { data, loading, error } = useGetEnrichedSavedPropertyIdsQuery();

    // handle errors
    const errorCode =
        data?.enrichedSavedPropertyIds?.__typename ===
        'EnrichedSavedPropertyIdsFail'
            ? data?.enrichedSavedPropertyIds?.errorCode
            : undefined;

    const [hasError, setHasError] = useState(false);
    useEffect(() => {
        // we aren't going to distinguish between network (Apollo) errors
        // and deliberately returned errorCode - but display a message for both
        if (error || errorCode) {
            setHasError(true);
        }
    }, [error, errorCode]);

    // extract enrichedSavedPropertyIds
    const enrichedSavedPropertyIds =
        data?.enrichedSavedPropertyIds?.__typename ===
        'EnrichedSavedPropertyIdsSuccess'
            ? data?.enrichedSavedPropertyIds?.enrichedSavedPropertyIds || []
            : [];

    // map section + category to correct SavedListingSection
    const mapped = enrichedSavedPropertyIds?.map(mapEnrichedSavedPropertyId);

    // we don't use expired listings for the widget
    const liveSavedListings = mapped?.filter(
        (listing) => listing.publicationStatus !== 'Expired'
    );

    return {
        savedListings: liveSavedListings,
        loading,
        hasError
    };
};
